import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';

import { IonContent } from '@ionic/angular';
import {
  BehaviorSubject,
  filter,
  of,
  pairwise,
  Subscription,
  switchMap,
  tap,
  catchError,
  take,
} from 'rxjs';

import { WebSocketEventsEnum } from '@shared/websocket';
import { Location } from '@shared/location/models';
import { extractPartyFieldsFromCustomer } from '@shared/customer/functions';
import { PartyTypes } from '@shared/party/enums';
import { isOnFreePlan } from '@shared/user/functions';
import { FrontendModalService } from '@tablesready/frontend-modal';
import { AppIcon, AppIconColor, AppIconSize } from '@tablesready/frontend-icons';
import { HeaderTitleService } from '@tablesready/frontend-header';
import { AuthRestaurantService, SocketService } from '@tablesready/frontend-store';
import { AlertsService, IAlertTypes } from '@tablesready/frontend-alerts-bar';
import {
  IParty,
  PageTitleService,
  OrderByValues,
  ConfirmationModalComponent,
  ConfirmationModalResult,
  SearchBarComponent,
} from '@tablesready/common-ui';
import { PartiesService, WaitTimeService } from '../services';
import { CustomersService } from './services';
import { AddEditCustomerComponent } from './modals/add-edit-customer/add-edit-customer.component';
import { VisitInfoCustomerComponent } from './modals/visit-info-customer/visit-info-customer.component';
import { ICustomer, ICustomersInputData } from './models';

enum AddEditCustomerMode {
  add = 'Add',
  edit = 'Edit',
}

@Component({
  selector: 'tablesready-customers',
  templateUrl: './customers.component.html',
  styleUrls: ['./customers.component.scss'],
})
export class CustomersComponent implements OnInit, OnDestroy {
  @ViewChild(IonContent) ionContent?: IonContent;
  @ViewChild('searchBarRef') searchBarRef?: SearchBarComponent;
  icon = AppIcon;
  size = AppIconSize;
  color = AppIconColor;

  userName: string;
  timezone: string;
  reservationLabel: string;
  isOpenedMenu = false;
  compact: boolean;
  reverse = false;
  customerListSortBy = 'name';
  searchText = '';
  page = 1;
  pages = 1;
  maxSize = 5;
  totalCustomers = 0;
  customers: ICustomer[] = [];
  msPerMin = 60000;
  perPage = 15;
  settings: Location;
  addEditCustomerMode = AddEditCustomerMode;
  customersInputData = new BehaviorSubject<ICustomersInputData>({
    page: this.page,
    sortBy: this.customerListSortBy,
    reverse: this.reverse,
    search: this.searchText,
  });

  private isLocationOnFreePlan = false;
  private readonly subscription = new Subscription();

  // eslint-disable-next-line max-params
  constructor(
    private readonly alertsService: AlertsService,
    private readonly customersService: CustomersService,
    private readonly socketService: SocketService,
    private readonly partiesService: PartiesService,
    private readonly waitTimeService: WaitTimeService,
    private readonly router: Router,
    private readonly modalController: FrontendModalService,
    private readonly pageTitleService: PageTitleService,
    private readonly authRestaurantService: AuthRestaurantService,
    private readonly headerService: HeaderTitleService,
  ) { }

  ionViewWillEnter(): void {
    void this.ionContent?.scrollToTop(0);
  }

  ionViewWillLeave(): void {
    this.resetFilters();
    if (!this.searchBarRef) {
      return;
    }

    this.searchBarRef.clearSearchField();
    this.unCheckAll();
  }

  updateSearchText(text: string): void {
    this.searchText = text;
    this.page = 1;
    this.loadMore();
  }

  ngOnInit(): void {
    this.pageTitleService.setTitle('Customers');

    this.subscription.add(this.customersInputData
      .pipe(switchMap(input => this.customersService.getCustomers(
        input.page,
        input.sortBy,
        input.reverse,
        input.search,
      )))
      .subscribe((response) => {
        this.customers = response.customers;
        this.totalCustomers = response.count;
        this.headerService.setDataForRoute('customers', { badgeCount: this.totalCustomers });
        this.pages = Math.ceil(this.totalCustomers / this.perPage);
      }));

    this.subscription.add(this.socketService
      .getEventStream(WebSocketEventsEnum.customers)
      .subscribe(() => {
        this.loadMore();
      }));

    this.subscription.add(this.authRestaurantService.selectWithAccount()
      .pipe(
        tap(({ account, location }) => {
          this.settings = location;
          this.userName = location.name;
          this.timezone = location.timezone;
          this.reservationLabel = location.displayOptions.labels.reservation;
          this.compact = location.displayOptions.compact;
          this.isLocationOnFreePlan = isOnFreePlan({ account, location });
          this.headerService.setDataForRoute('customers', { title: location.displayOptions.labels.customer_plural });
          this.waitTimeService.setTime(
            this.settings.default_quoted_time,
            this.settings.default_quoted_time,
          );
        }),
        pairwise(),
        filter(([prev, current]) => prev.location._id !== current.location._id),
        tap(() => {
          this.loadMore();
        }),
      )
      .subscribe());
  }

  loadMore(): void {
    this.customersInputData.next({
      page: this.page,
      sortBy: this.customerListSortBy,
      reverse: this.reverse,
      search: this.searchText,
    });
  }

  orderBy({ orderBy, orderAscending }: OrderByValues): void {
    this.reverse = orderAscending;
    this.customerListSortBy = orderBy;
    this.unCheckAll();
    this.loadMore();
  }

  pageChange(page: number): void {
    this.page = page;
    this.loadMore();
  }

  toggleDropDown(): void {
    this.isOpenedMenu = !this.isOpenedMenu;
  }

  checkAll(): void {
    this.customers.forEach((customer) => {
      customer.selected = true;
    });
    this.isOpenedMenu = false;
  }

  unCheckAll(): void {
    this.customers.forEach((customer) => {
      customer.selected = false;
    });
    this.isOpenedMenu = false;
  }

  bulkActionDisabled(): boolean {
    return !this.customers.filter(customer => customer.selected).length;
  }

  addEditCustomer(type: AddEditCustomerMode, id?: string): void {
    (id ? this.customersService.getCustomerById(id) : of({})).subscribe((response) => {
      this.modalController.create<AddEditCustomerComponent, unknown>(AddEditCustomerComponent, {
        title: `${type} ${this.settings.displayOptions.labels.customer}`,
        customer: response,
        editMode: type === AddEditCustomerMode.edit,
        isOnFreePlan: this.isLocationOnFreePlan,
        settings: {
          country: this.settings.country,
          customer: this.settings.displayOptions.labels.customer,
          customFields: this.settings.partyFields,
        },
      })
        .pipe(take(1))
        .subscribe();
    });
  }

  addToWaitlist(customer) {
    const now = new Date().getTime();
    const party: IParty = {
      name: customer.name,
      phone: customer.phone,
      size: this.settings.default_size,
      created_time: now,
      checkin_time: now,
      quoted_time: now + this.waitTimeService.getTime() * this.msPerMin,
      type: PartyTypes.walkIn,
      note: '',
      ...extractPartyFieldsFromCustomer(this.settings.partyFields, customer),
    };
    this.partiesService.saveParty(party).subscribe(
      () => {
        this.alertsService.add({
          type: IAlertTypes.success,
          heading: 'Success:',
          body: `new ${this.settings.displayOptions.labels.customer} added to waitlist.`,
        });
      },
      () => {
        this.alertsService.add({

          type: IAlertTypes.danger,
          heading: 'Error:',
          body: `new ${this.settings.displayOptions.labels.customer} not added successfully.`,
        });
      },
    );
  }

  addSelectedToWaitlist() {
    const now = Date.now();
    const parties = [];

    this.customers.forEach((customer) => {
      if (customer.selected) {
        const party = {
          phone: customer.phone,
          size: this.settings.default_size,
          created_time: now,
          checkin_time: now,
          quoted_time: now + this.waitTimeService.getTime() * this.msPerMin,
          type: PartyTypes.walkIn,
          note: '',
          ...extractPartyFieldsFromCustomer(this.settings.partyFields, customer),
        };
        parties.push(party);
      }
    });

    if (parties.length > 0) {
      this.partiesService.savePartiesBulk(parties, true).subscribe(
        (response) => {
          this.alertsService.add({

            type: IAlertTypes.success,
            heading: 'Success:',
            body: `${response.saved} new ${this.settings.displayOptions.labels.customer_plural} added to waitlist.`,
          });
        },
        () => {
          this.alertsService.add({

            type: IAlertTypes.danger,
            heading: 'Error:',
            body: `new ${this.settings.displayOptions.labels.customer_plural} not added successfully.`,
          });
        },
      );
    }
  }

  deleteSelected(id?: string): void {
    let customersId: string[] = [];
    if (id) {
      customersId = [id];
    } else {
      customersId = this.customers
        .filter(customer => customer.selected)
        .map(customer => customer._id);
    }

    const label = this.settings.displayOptions.labels.customer;
    const titleLabel = customersId.length > 1 ? `${label}s` : label;
    const textLabel = customersId.length > 1 ? `these ${customersId.length} ${label}s` : `this ${label}`;
    this.modalController.create<ConfirmationModalComponent, ConfirmationModalResult<boolean>>(ConfirmationModalComponent, {
      options: {
        title: `Delete ${titleLabel}`,
        content: `Are you sure you want to delete ${textLabel}?`,
      },
    })
      .pipe(
        take(1),
        filter((res) => res?.response),
        switchMap(() => this.customersService.deleteCustomersBulk(customersId)),
        tap(() => {
          this.alertsService.addSuccess(`deleted ${titleLabel}.`, 'Deleted: ');
        }),
        catchError(() => {
          this.alertsService.addError(`${titleLabel} not deleted successfully.`, 'Error: ');

          return of(null);
        }),
      )
      .subscribe();
  }

  viewInfoCustomer(id: string): void {
    this.customersService.getCustomerById(id).subscribe((response) => {
      this.modalController.create<VisitInfoCustomerComponent, unknown>(VisitInfoCustomerComponent, {
        customer: response,
        timezone: this.timezone,
        reservationLabel: this.reservationLabel,
      })
        .pipe(take(1))
        .subscribe();
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private resetFilters(): void {
    this.page = 1;
    this.customerListSortBy = 'name';
    this.reverse = false;
    this.searchText = '';

    this.customersInputData.next({
      page: this.page,
      sortBy: this.customerListSortBy,
      reverse: this.reverse,
      search: this.searchText,
    });
  }
}
